import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { isMobile, isTablet } from '../../utils/deviceUtils';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const mobile = isMobile();
  const tablet = isTablet();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // TODO: Implement actual contact form submission
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-500 to-primary-600 text-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 p-4 sm:p-6 lg:p-8">
          <div className="text-center">
            <h1 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-black mb-4`}>
              Get in Touch
            </h1>
            <p className={`${mobile ? 'text-base' : 'text-lg'} text-white/90 max-w-2xl mx-auto`}>
              Have questions about our safari packages? Need help with your booking? 
              We're here to help you plan your perfect African adventure.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-background-primary`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid ${mobile ? 'grid-cols-1 gap-8' : tablet ? 'grid-cols-1 lg:grid-cols-2 gap-12' : 'grid-cols-1 lg:grid-cols-2 gap-16'}`}>
            
            {/* Contact Information */}
            <div className="p-4 sm:p-6 lg:p-8">
              <h2 className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-text-primary mb-6`}>
                Contact Information
              </h2>
              <p className="text-text-secondary mb-8 leading-relaxed">
                Our team of safari experts is ready to help you plan your dream adventure. 
                Reach out to us through any of the channels below.
              </p>

              <div className="space-y-6">
                {/* Office Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">Office Address</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      123 Safari Street<br />
                      Arusha, Tanzania<br />
                      East Africa
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">Phone</h3>
                    <p className="text-text-secondary text-sm">
                      <a href="tel:+255123456789" className="hover:text-primary-500 transition-colors">
                        +255 123 456 789
                      </a>
                    </p>
                    <p className="text-text-secondary text-sm">
                      <a href="tel:+255987654321" className="hover:text-primary-500 transition-colors">
                        +255 987 654 321
                      </a>
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">Email</h3>
                    <p className="text-text-secondary text-sm">
                      <a href="mailto:info@dalarides.com" className="hover:text-primary-500 transition-colors">
                        info@dalarides.com
                      </a>
                    </p>
                    <p className="text-text-secondary text-sm">
                      <a href="mailto:support@dalarides.com" className="hover:text-primary-500 transition-colors">
                        support@dalarides.com
                      </a>
                    </p>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">Business Hours</h3>
                    <p className="text-text-secondary text-sm">
                      Monday - Friday: 8:00 AM - 6:00 PM<br />
                      Saturday: 9:00 AM - 4:00 PM<br />
                      Sunday: 10:00 AM - 2:00 PM<br />
                      <span className="text-primary-500 font-medium">24/7 Emergency Support Available</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="mt-8 p-4 bg-accent-warning-50 rounded-xl border border-accent-warning-200">
                <h3 className="font-semibold text-accent-warning-800 mb-2">Emergency Contact</h3>
                <p className="text-accent-warning-700 text-sm mb-2">
                  For urgent matters during your safari:
                </p>
                <p className="text-accent-warning-800 font-medium">
                  Emergency: +255 999 888 777
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="p-4 sm:p-6 lg:p-8">
              <h2 className={`${mobile ? 'text-2xl' : 'text-3xl'} font-bold text-text-primary mb-6`}>
                Send us a Message
              </h2>
              
              {success ? (
                <div className="bg-accent-success-50 border border-accent-success-200 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-accent-success-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Send className="w-6 h-6 text-accent-success-600" />
                  </div>
                  <h3 className="font-semibold text-accent-success-800 mb-2">Message Sent!</h3>
                  <p className="text-accent-success-700 text-sm">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-text-primary mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border-primary bg-surface-primary text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border-primary bg-surface-primary text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-text-primary mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border-primary bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select a subject</option>
                      <option value="safari-inquiry">Safari Inquiry</option>
                      <option value="booking-help">Booking Help</option>
                      <option value="custom-package">Custom Package Request</option>
                      <option value="transport-inquiry">Transport Inquiry</option>
                      <option value="general-question">General Question</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 rounded-xl border border-border-primary bg-surface-primary text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-vertical"
                      placeholder="Tell us about your inquiry or question..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary-500 text-white py-3 px-6 rounded-xl font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}; 